import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { 
  X, Loader2, ChevronRight, Map, Target,
  Clock, Layers, StickyNote, RefreshCw,
  FileUp, Edit3, Flag, Sparkles, Hash, LayoutList
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

/**
 * REVIEW V1 PHILOSOPHY:
 * The transcript is the source of truth.
 * Review answers two questions only:
 *   1. What did I actually do on stage?
 *   2. Where should I take this back into writing?
 * 
 * ❌ No joke rewriting
 * ❌ No punchline suggestions  
 * ❌ No funniness scoring
 * ❌ No delivery coaching
 * ❌ No comparison to other comics
 */

// ============ SET MAP (Navigation Only) ============
const SetMap = ({ 
  segments, 
  activeSegmentId, 
  onSegmentClick, 
  transcriptionId,
  onCreateAllBits,
  isCreatingBits,
  onRunAnalysis,
  isAnalyzing,
  analysisExists
}) => {
  // No analysis yet - show option to run it
  if (!analysisExists) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <div className="bg-muted/30 rounded-sm p-4">
          {isAnalyzing ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm font-medium">Analyzing transcript...</p>
              <p className="text-xs mt-1">This may take 30-60 seconds</p>
            </>
          ) : (
            <>
              <Map className="w-6 h-6 mx-auto mb-2 opacity-50" />
              <p className="text-sm font-medium mb-2">No analysis yet</p>
              <Button
                variant="default"
                size="sm"
                onClick={onRunAnalysis}
                className="gap-1"
              >
                <Sparkles className="w-3 h-3" />
                Analyze Transcript
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Analysis exists but no segments found
  if (!segments || segments.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        <p>No segments detected in this transcript.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={onRunAnalysis}
          disabled={isAnalyzing}
          className="mt-2 gap-1"
        >
          {isAnalyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Re-analyze
        </Button>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      {/* Bulk Action Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Set Map
        </h4>
        <Button
          variant="outline"
          size="sm"
          onClick={onCreateAllBits}
          disabled={isCreatingBits}
          className="text-xs h-7 gap-1"
        >
          {isCreatingBits ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Sparkles className="w-3 h-3" />
          )}
          Create Bits from Segments
        </Button>
      </div>

      {/* Segment List - Navigation Only */}
      <div className="space-y-1">
        {segments.map((segment, idx) => {
          // Support both old format (joke_id) and new format (bit_id)
          const segmentId = segment.joke_id || segment.bit_id;
          const segmentTitle = segment.title || segment.topic || 'Untitled';
          
          return (
            <button
              key={segmentId || idx}
              onClick={() => onSegmentClick(segment)}
              className={`
                w-full text-left p-2 rounded-sm border transition-all
                ${activeSegmentId === segmentId 
                  ? 'border-primary bg-primary/10' 
                  : 'border-transparent hover:border-border hover:bg-muted/30'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-mono w-6">
                  {idx + 1}.
                </span>
                <span className="text-sm truncate flex-1">
                  {segmentTitle}
                </span>
                {segment.timestamp && (
                  <span className="text-xs text-muted-foreground">
                    {segment.timestamp}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============ SEGMENT DETAIL (Mechanical Facts Only) ============
const SegmentDetail = ({ segment, rawSegments }) => {
  if (!segment) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <Target className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Select a segment from the Set Map</p>
      </div>
    );
  }

  // Get the raw segments/spans for this bit (support both old and new formats)
  const bitId = segment.joke_id || segment.bit_id;
  const bitSpans = rawSegments?.filter(s => 
    s.joke_group_id === bitId || s.bit_id === bitId
  ) || [];
  
  // Count by type - simplified 3 categories
  const setupSpans = bitSpans.filter(s => 
    s.type === 'SETUP' || s.segment_type === 'setup'
  );
  const punchSpans = bitSpans.filter(s => 
    s.type === 'PUNCH' || s.segment_type === 'punchline' || s.type === 'TAG'
  );
  const hostingSpans = bitSpans.filter(s => 
    s.type === 'HOSTING' || s.type === 'BANTER_LIGHT' || s.type === 'TRANSITION'
  );

  // Calculate word counts
  const setupWordCount = setupSpans.reduce((acc, s) => acc + (s.word_count || 0), 0);
  const totalWordCount = bitSpans.reduce((acc, s) => acc + (s.word_count || 0), 0);

  return (
    <div className="p-3 space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Bit Detail
      </h4>

      <div className="space-y-2">
        {/* Segment Title */}
        <div className="bg-muted/30 rounded-sm p-2">
          <p className="font-medium text-sm">{segment.title || segment.topic}</p>
          {segment.timestamp && (
            <p className="text-xs text-muted-foreground mt-1">{segment.timestamp}</p>
          )}
        </div>

        {/* Mechanical Facts Grid */}
        <div className="grid grid-cols-2 gap-2">
          {/* Setup Length - Warm charcoal */}
          <div className="rounded-sm p-2 border-l-2 border-gray-500" style={{ backgroundColor: '#1F2933' }}>
            <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
              <Clock className="w-3 h-3" />
              Setup
            </div>
            <p className="text-sm font-mono">
              {segment.setup_word_count || setupWordCount || 0} words
            </p>
          </div>

          {/* Hosting - Muted gray */}
          <div className="bg-muted/20 rounded-sm p-2">
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
              <ChevronRight className="w-3 h-3" />
              Hosting
            </div>
            <p className="text-sm font-mono">
              {hostingSpans.length || 0}
            </p>
          </div>

          {/* Punches - Original bright yellow */}
          <div className="bg-primary/10 rounded-sm p-2">
            <div className="flex items-center gap-1 text-xs text-primary mb-1">
              <Target className="w-3 h-3" />
              Punchlines
            </div>
            <p className="text-sm font-mono">
              {segment.punch_count || punchSpans.length || 0}
            </p>
          </div>

          {/* Total Words */}
          <div className="bg-muted/20 rounded-sm p-2">
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
              <Hash className="w-3 h-3" />
              Total
            </div>
            <p className="text-sm font-mono">
              {totalWordCount} words
            </p>
          </div>
        </div>

        {/* Span type breakdown - warm neutral badges (punchline stays yellow) */}
        {bitSpans.length > 0 && (
          <div className="text-xs text-muted-foreground pt-2 border-t border-border">
            <div className="flex flex-wrap gap-2">
              {setupSpans.length > 0 && (
                <span className="px-1.5 py-0.5 rounded text-gray-300" style={{ backgroundColor: '#1F2933' }}>{setupSpans.length} setup</span>
              )}
              {punchSpans.length > 0 && (
                <span className="bg-primary/20 px-1.5 py-0.5 rounded text-primary">{punchSpans.length} punch</span>
              )}
              {hostingSpans.length > 0 && (
                <span className="bg-muted/30 px-1.5 py-0.5 rounded text-gray-500">{hostingSpans.length} hosting</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ DENSITY (Directional Only) ============
const DensityIndicator = ({ segment, allSegments }) => {
  if (!segment || !allSegments || allSegments.length === 0) {
    return null;
  }

  // Calculate density relative to other segments
  const avgPunches = allSegments.reduce((acc, s) => acc + (s.punch_count || 0), 0) / allSegments.length;
  const segmentPunches = segment.punch_count || 0;
  
  const isDense = segmentPunches > avgPunches;
  const isSparse = segmentPunches < avgPunches * 0.5;

  return (
    <div className="p-3 space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Density
      </h4>
      <div className={`
        p-3 rounded-sm text-center transition-all
        ${isDense ? 'bg-primary/10 text-primary' : isSparse ? 'bg-muted/30 text-muted-foreground' : 'bg-muted/20'}
      `}>
        <p className="text-sm font-medium">
          {isDense ? 'Dense' : isSparse ? 'Sparse' : 'Average'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {isDense 
            ? 'High punch frequency' 
            : isSparse 
              ? 'More setup than payoff' 
              : 'Standard rhythm'
          }
        </p>
      </div>
    </div>
  );
};

// ============ ACTIONS PANEL ============
const ActionsPanel = ({ 
  segment, 
  selectedText,
  onExtractAsSection, 
  onExtractAsNewBit,
  onAddNote, 
  onMarkForRewrite,
  bits
}) => {
  const hasSelection = selectedText && selectedText.trim().length > 0;
  const hasSegment = segment !== null;
  const isActive = hasSelection || hasSegment;

  if (!isActive) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">Select text in the transcript or a segment from the Set Map to see actions</p>
      </div>
    );
  }

  const actionTarget = hasSelection ? "selection" : "segment";

  return (
    <div className="p-3 space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Actions {hasSelection && <span className="text-primary">(Selection)</span>}
      </h4>

      <div className="space-y-2">
        {/* Extract as Section - PRIMARY */}
        <Button 
          variant="default" 
          size="sm" 
          className="w-full justify-start gap-2"
          onClick={() => onExtractAsSection(segment, selectedText)}
        >
          <Layers className="w-4 h-4" />
          Extract as Section
        </Button>
        
        {/* Extract as New Bit */}
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full justify-start gap-2"
          onClick={() => onExtractAsNewBit(segment, selectedText)}
        >
          <FileUp className="w-4 h-4" />
          Extract as New Bit
        </Button>
        
        {/* Add Performance Note */}
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full justify-start gap-2"
          onClick={() => onAddNote(segment, selectedText)}
        >
          <StickyNote className="w-4 h-4" />
          Add Performance Note
        </Button>
        
        {/* Mark for Rewrite */}
        {hasSegment && !hasSelection && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start gap-2 text-muted-foreground"
            onClick={() => onMarkForRewrite(segment)}
          >
            <Flag className="w-4 h-4" />
            Mark for Rewrite
          </Button>
        )}
      </div>
    </div>
  );
};

// ============ INTERACTIVE TRANSCRIPT ============
const InteractiveTranscript = ({ 
  transcript, 
  analysis,
  activeSegmentId,
  onSegmentClick,
  onTextSelect,
  scrollTargetRef,
  filterType,
  onFilterChange
}) => {
  const transcriptRef = useRef(null);
  const [selectedRange, setSelectedRange] = useState(null);

  // Handle text selection
  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) {
      const text = selection.toString().trim();
      setSelectedRange(text);
      onTextSelect(text);
    } else {
      setSelectedRange(null);
      onTextSelect(null);
    }
  }, [onTextSelect]);

  // Build enhanced transcript with markers using character indices
  const enhancedContent = useMemo(() => {
    if (!analysis || !analysis.segments || analysis.segments.length === 0) {
      return [{ type: 'text', content: transcript, id: 'raw' }];
    }

    const segments = analysis.segments;
    const jokes = analysis.jokes || [];
    const bits = analysis.bits || [];
    
    // Create a map from segment_id/span_id to joke_id/bit_id for quick lookup
    // Support both old format (jokes[].segments[]) and new format (span.bit_id)
    const segmentToJoke = {};
    
    // Old format: jokes have segments array
    jokes.forEach(joke => {
      (joke.segments || []).forEach(segId => {
        segmentToJoke[segId] = joke.joke_id;
      });
    });
    
    // New format: bits have segments array (from backend compatibility layer)
    bits.forEach(bit => {
      (bit.segments || []).forEach(segId => {
        segmentToJoke[segId] = bit.bit_id || bit.joke_id;
      });
    });

    const parts = [];
    let lastEnd = 0;
    
    // Sort segments by their start_index (use index-based positioning)
    const sortedSegments = [...segments]
      .filter(seg => {
        // Use start_index/end_index if available, otherwise fall back to text matching
        if (typeof seg.start_index === 'number' && typeof seg.end_index === 'number') {
          return seg.start_index >= 0 && seg.end_index > seg.start_index && seg.end_index <= transcript.length;
        }
        // Fallback: try to find text in transcript
        return seg.text && transcript.includes(seg.text);
      })
      .sort((a, b) => {
        const aIdx = typeof a.start_index === 'number' ? a.start_index : transcript.indexOf(a.text);
        const bIdx = typeof b.start_index === 'number' ? b.start_index : transcript.indexOf(b.text);
        return aIdx - bIdx;
      });

    sortedSegments.forEach((segment, idx) => {
      // Determine segment position using indices or fallback to text search
      let segmentStart, segmentEnd;
      
      if (typeof segment.start_index === 'number' && typeof segment.end_index === 'number') {
        // Use index-based positioning (preferred, reliable)
        segmentStart = segment.start_index;
        segmentEnd = segment.end_index;
      } else if (segment.text) {
        // Fallback to text search (legacy support)
        segmentStart = transcript.indexOf(segment.text, lastEnd);
        if (segmentStart === -1) return;
        segmentEnd = segmentStart + segment.text.length;
      } else {
        return;
      }
      
      // Skip if this segment overlaps with previous content
      if (segmentStart < lastEnd) {
        return;
      }
      
      // Add text before this segment
      if (segmentStart > lastEnd) {
        parts.push({
          type: 'text',
          content: transcript.slice(lastEnd, segmentStart),
          id: `text-${idx}`
        });
      }
      
      // Extract segment text from transcript using indices
      const segmentText = transcript.slice(segmentStart, segmentEnd);
      
      // Get the jokeId - try multiple lookup keys since backend uses different field names
      const jokeId = segmentToJoke[segment.segment_id] || 
                     segmentToJoke[segment.span_id] || 
                     segment.joke_group_id || 
                     segment.bit_id;
      
      // Add the segment with markers
      parts.push({
        type: 'segment',
        content: segmentText,
        segment: segment,
        jokeId: jokeId,
        // Support both old and new type formats
        spanType: segment.type || segment.segment_type?.toUpperCase() || 'SETUP',
        isPunchline: segment.type === 'PUNCH' || segment.segment_type === 'punchline',
        isTurn: segment.type === 'TURN' || segment.is_turn,
        isHosting: segment.type === 'HOSTING' || segment.type === 'BANTER_LIGHT',
        isTransition: segment.type === 'TRANSITION',
        isSerious: segment.type === 'INFO_SERIOUS',
        confidence: segment.confidence || 1.0,
        id: segment.segment_id || segment.span_id
      });
      
      lastEnd = segmentEnd;
    });

    // Add remaining text
    if (lastEnd < transcript.length) {
      parts.push({
        type: 'text',
        content: transcript.slice(lastEnd),
        id: 'text-end'
      });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: transcript, id: 'raw' }];
  }, [transcript, analysis]);

  // Find if any part should be highlighted based on activeSegmentId
  const isActiveJoke = useCallback((jokeId) => {
    return jokeId === activeSegmentId;
  }, [activeSegmentId]);

  // Get span type styling classes - Simplified 3-category system
  // Dark UI Warm Neutral Palette:
  // PUNCHLINE: Original bright yellow (primary) - user preferred
  // SETUP: Warm charcoal (#1F2933) with optional gray border (#6B7280)
  // HOSTING: Transparent/dark (#0F1419) with muted gray text (#6B7280)
  const getSpanClasses = (part, isActive, currentFilter) => {
    const baseClasses = "cursor-pointer transition-all rounded-sm px-0.5 -mx-0.5";
    const activeClasses = isActive ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : "";
    
    let typeClasses = "";
    const isPunchType = ['PUNCH', 'TAG', 'TURN'].includes(part.spanType);
    const isSetupType = part.spanType === 'SETUP';
    const isHostingType = ['HOSTING', 'BANTER_LIGHT', 'TRANSITION', 'INFO_SERIOUS'].includes(part.spanType);
    
    // Filter-aware styling
    if (currentFilter === 'PUNCH') {
      // Show Punchlines mode: Punchlines full highlight, Setup no background, Hosting muted
      if (isPunchType) {
        typeClasses = "font-semibold text-primary bg-primary/20"; // Original bright yellow
      } else if (isSetupType) {
        typeClasses = ""; // No background
      } else if (isHostingType) {
        typeClasses = "text-gray-500"; // Muted text
      }
    } else if (currentFilter === 'SETUP') {
      // Show Setup mode: Setup full charcoal, Punchlines thin underline, Hosting muted
      if (isSetupType) {
        typeClasses = "border-l-2 border-gray-500";
        // Inline style will handle bg color
      } else if (isPunchType) {
        typeClasses = "underline decoration-primary decoration-2 underline-offset-2"; // Thin yellow underline
      } else if (isHostingType) {
        typeClasses = "text-gray-500"; // Muted text
      }
    } else if (currentFilter === 'HOSTING') {
      // Show Hosting mode: Hosting faint dark-gray background, Others no background
      if (isHostingType) {
        typeClasses = "text-gray-500";
        // Inline style will handle bg color
      } else {
        typeClasses = ""; // No background for others
      }
    } else {
      // ALL mode - show all with their proper styling
      if (isPunchType) {
        typeClasses = "font-semibold text-primary bg-primary/20"; // Original bright yellow
      } else if (isSetupType) {
        typeClasses = "border-l-2 border-gray-500";
      } else if (isHostingType) {
        typeClasses = "text-gray-500";
      }
    }
    
    return `${baseClasses} ${activeClasses} ${typeClasses}`;
  };
  
  // Get inline styles for background colors (hex colors not in Tailwind)
  const getSpanStyle = (part, currentFilter) => {
    const isPunchType = ['PUNCH', 'TAG', 'TURN'].includes(part.spanType);
    const isSetupType = part.spanType === 'SETUP';
    const isHostingType = ['HOSTING', 'BANTER_LIGHT', 'TRANSITION', 'INFO_SERIOUS'].includes(part.spanType);
    
    if (currentFilter === 'PUNCH') {
      // Punchlines use Tailwind class bg-primary/20, no inline style needed
      if (isSetupType) return {};
      return {};
    } else if (currentFilter === 'SETUP') {
      if (isSetupType) return { backgroundColor: '#1F2933' }; // Warm charcoal
      return {};
    } else if (currentFilter === 'HOSTING') {
      if (isHostingType) return { backgroundColor: '#0F1419' }; // Faint dark gray
      return {};
    } else {
      // ALL mode
      if (isSetupType) return { backgroundColor: '#1F2933' }; // Warm charcoal  
      if (isHostingType) return { backgroundColor: 'transparent' }; // Recedes
      return {};
    }
  };

  // Get span type indicator - Punchline uses original bright yellow
  const getSpanIndicator = (part) => {
    switch (part.spanType) {
      case 'PUNCH':
      case 'TAG':
      case 'TURN':
        return <span className="text-primary mr-0.5">●</span>;
      case 'HOSTING':
      case 'BANTER_LIGHT':
      case 'TRANSITION':
      case 'INFO_SERIOUS':
        return <span className="text-gray-500 mr-0.5 text-xs">MC</span>;
      default:
        return null;
    }
  };

  // Filter types for highlighting - Simplified 3-category system
  // Punchline: original bright yellow, Setup/Hosting: warm neutral palette
  const filterOptions = [
    { type: 'ALL', label: 'All', color: 'bg-gray-500' },
    { type: 'PUNCH', label: 'Punchline', color: 'bg-primary' },     // Original bright yellow
    { type: 'SETUP', label: 'Setup', color: 'bg-gray-500' },        // Structural, calm
    { type: 'HOSTING', label: 'Hosting', color: 'bg-gray-600' },    // Recedes
  ];

  // Check if a span should be highlighted based on filter
  const shouldHighlight = (spanType) => {
    if (!filterType || filterType === 'ALL') return true;
    // Handle legacy types - map them to simplified categories
    if (filterType === 'HOSTING' && ['HOSTING', 'BANTER_LIGHT', 'TRANSITION', 'INFO_SERIOUS'].includes(spanType)) return true;
    if (filterType === 'PUNCH' && ['PUNCH', 'TAG', 'TURN'].includes(spanType)) return true;
    return spanType === filterType;
  };

  return (
    <div 
      ref={transcriptRef}
      className="font-mono text-sm leading-relaxed p-4"
      onMouseUp={handleMouseUp}
    >
      {/* Filter Buttons - Click to filter by type */}
      {analysis && analysis.segments && analysis.segments.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs mb-4 pb-3 border-b border-border">
          <span className="text-muted-foreground mr-1">Show:</span>
          {filterOptions.map(option => (
            <button
              key={option.type}
              onClick={() => onFilterChange?.(filterType === option.type ? 'ALL' : option.type)}
              className={`
                flex items-center gap-1.5 px-2 py-1 rounded-md transition-all
                ${filterType === option.type 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted/50 hover:bg-muted text-muted-foreground'
                }
              `}
            >
              <span className={`w-2 h-2 rounded-full ${option.color}`}></span>
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Transcript Content */}
      {enhancedContent.map((part) => {
        if (part.type === 'text') {
          return (
            <span key={part.id} className="whitespace-pre-wrap">
              {part.content}
            </span>
          );
        }

        if (part.type === 'segment') {
          const isActive = isActiveJoke(part.jokeId);
          const isFiltered = shouldHighlight(part.spanType);
          
          // If filtered out, render as plain text (still selectable)
          if (!isFiltered) {
            return (
              <span key={part.id} className="whitespace-pre-wrap">
                {part.content}
              </span>
            );
          }
          
          // Simple tooltip - just show the type
          const tooltipText = part.spanType;
          
          return (
            <span
              key={part.id}
              ref={isActive ? scrollTargetRef : null}
              className={getSpanClasses(part, isActive, filterType)}
              style={getSpanStyle(part, filterType)}
              title={tooltipText}
            >
              {getSpanIndicator(part)}
              {part.content}
            </span>
          );
        }

        return null;
      })}
    </div>
  );
};

// ============ MAIN SET REVIEW COMPONENT ============
export const SetReview = ({
  performance,
  onClose,
  bits,
  onBitCreated,
  onSwitchToWrite
}) => {
  const [activeSegmentId, setActiveSegmentId] = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCreatingBits, setIsCreatingBits] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [filterType, setFilterType] = useState('ALL'); // Filter for highlighting: ALL, PUNCH, SETUP, etc.
  const [viewMode, setViewMode] = useState('analysis'); // 'original' or 'analysis'
  
  // Dialog states
  const [isExtractDialogOpen, setIsExtractDialogOpen] = useState(false);
  const [extractMode, setExtractMode] = useState("section"); // "section" | "new"
  const [extractData, setExtractData] = useState(null);
  const [selectedBitId, setSelectedBitId] = useState("");
  
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteTarget, setNoteTarget] = useState(null);
  
  const scrollTargetRef = useRef(null);
  const pollingRef = useRef(null);

  // Get analysis from performance
  const analysis = performance?.analysis;
  const analysisExists = analysis !== null && analysis !== undefined;
  const jokes = useMemo(() => analysis?.jokes || [], [analysis]);
  const analysisBits = useMemo(() => analysis?.bits || [], [analysis]);
  const segments = useMemo(() => analysis?.segments || [], [analysis]);
  const markedUpTranscript = analysis?.marked_up_transcript || null;

  // Poll for analysis completion if analysis doesn't exist yet
  useEffect(() => {
    // If analysis doesn't exist, start polling
    if (!analysisExists && performance?.transcription_id) {
      const pollForAnalysis = async () => {
        try {
          const response = await fetch(
            `${API}/transcriptions/${performance.transcription_id}`,
            { credentials: 'include' }
          );
          if (response.ok) {
            const data = await response.json();
            if (data.analysis) {
              // Analysis is ready, refresh the parent
              onBitCreated?.();
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
            }
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      };

      // Poll every 3 seconds
      pollingRef.current = setInterval(pollForAnalysis, 3000);
      // Also poll immediately
      pollForAnalysis();

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      };
    }
  }, [analysisExists, performance?.transcription_id, onBitCreated]);

  // Active segment data
  const activeSegment = useMemo(() => {
    return jokes.find(j => j.joke_id === activeSegmentId) || 
           analysisBits.find(b => b.bit_id === activeSegmentId) || null;
  }, [jokes, analysisBits, activeSegmentId]);

  // Handle segment click from Set Map or Transcript
  const handleSegmentClick = useCallback((segment) => {
    // Support both old format (joke_id) and new format (bit_id)
    const segmentId = segment.joke_id || segment.bit_id;
    setActiveSegmentId(segmentId);
    setSelectedText(null);
    
    // Scroll to segment in transcript
    setTimeout(() => {
      scrollTargetRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }, 100);
  }, []);

  // Handle text selection in transcript
  const handleTextSelect = useCallback((text) => {
    setSelectedText(text);
  }, []);

  // Run or refresh analysis
  const handleRunAnalysis = async () => {
    if (!performance?.transcription_id) return;
    setIsAnalyzing(true);
    
    try {
      // Call the analyze endpoint
      const response = await fetch(
        `${API}/transcriptions/${performance.transcription_id}/analyze`,
        { method: 'POST', credentials: 'include' }
      );

      if (response.ok) {
        toast.success("Analysis started - this may take a moment");
        // Start polling for completion
        const pollForAnalysis = async () => {
          const checkResponse = await fetch(
            `${API}/transcriptions/${performance.transcription_id}`,
            { credentials: 'include' }
          );
          if (checkResponse.ok) {
            const data = await checkResponse.json();
            if (data.analysis) {
              setIsAnalyzing(false);
              onBitCreated?.();
              return true;
            }
          }
          return false;
        };

        // Poll every 2 seconds for up to 60 seconds
        let attempts = 0;
        const maxAttempts = 30;
        const pollInterval = setInterval(async () => {
          attempts++;
          const done = await pollForAnalysis();
          if (done || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            if (!done) {
              setIsAnalyzing(false);
              toast.error("Analysis is taking longer than expected. Please try refreshing.");
            }
          }
        }, 2000);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start analysis");
        setIsAnalyzing(false);
      }
    } catch (error) {
      toast.error("Failed to start analysis");
      setIsAnalyzing(false);
    }
  };

  // Refresh analysis (delete and re-run)
  const handleRefreshAnalysis = async () => {
    if (!performance?.transcription_id) return;
    setIsRefreshing(true);
    
    try {
      // Delete existing analysis using POST endpoint
      await fetch(`${API}/transcriptions/${performance.transcription_id}/analysis/delete`, {
        method: 'POST',
        credentials: 'include'
      });
      
      // Run analysis again
      await handleRunAnalysis();
    } catch (error) {
      toast.error("Failed to refresh analysis");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Create bits from all segments
  const handleCreateAllBits = async () => {
    if (!performance?.transcription_id) return;
    setIsCreatingBits(true);
    
    try {
      const response = await fetch(`${API}/review/create-bits-from-segments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ transcription_id: performance.transcription_id })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Created ${data.count} bits from performance!`);
        onBitCreated?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create bits");
      }
    } catch (error) {
      toast.error("Failed to create bits");
    } finally {
      setIsCreatingBits(false);
    }
  };

  // Open extract dialog
  const handleExtractAsSection = (segment, text) => {
    // Prefer selected text over segment text
    const content = text && text.trim() ? text.trim() : getSegmentFullText(segment);
    const title = segment?.title || "From Performance";
    
    if (!content || content.startsWith("[Segment:")) {
      toast.error("Please select text in the transcript to extract");
      return;
    }
    
    setExtractData({ content, title, segment });
    setExtractMode("section");
    setSelectedBitId(bits?.[0]?.bit_id || "");
    setIsExtractDialogOpen(true);
  };

  const handleExtractAsNewBit = (segment, text) => {
    // Prefer selected text over segment text
    const content = text && text.trim() ? text.trim() : getSegmentFullText(segment);
    const title = segment?.title || "Extracted Material";
    
    if (!content || content.startsWith("[Segment:")) {
      toast.error("Please select text in the transcript to extract");
      return;
    }
    
    setExtractData({ content, title, segment });
    setExtractMode("new");
    setIsExtractDialogOpen(true);
  };

  // Get full text for a joke/segment
  const getSegmentFullText = useCallback((joke) => {
    if (!joke) return "";
    
    // First try to get text from the segment analysis
    const jokeSegmentIds = joke.segments || [];
    if (jokeSegmentIds.length > 0 && segments.length > 0) {
      const text = jokeSegmentIds
        .map(sid => segments.find(s => s.segment_id === sid)?.text || "")
        .filter(t => t)
        .join("\n\n")
        .trim();
      if (text) return text;
    }
    
    // Fallback: If joke has a title, try to find that section in the transcript
    // This helps when analysis structure doesn't match expected format
    if (joke.title && performance?.transcript) {
      // Look for the segment in the full transcript (basic search)
      const transcript = performance.transcript;
      // Return the joke title as a marker - user should select text manually
      return `[Segment: ${joke.title}] - Please select the relevant text from the transcript`;
    }
    
    return "";
  }, [segments, performance?.transcript]);

  // Confirm extract action
  const handleConfirmExtract = async () => {
    if (!extractData) return;
    
    // Validate content exists
    if (!extractData.content || extractData.content.trim() === "") {
      toast.error("No content to extract. Select text in the transcript first.");
      return;
    }
    
    try {
      if (extractMode === "section" && selectedBitId) {
        const response = await fetch(`${API}/review/extract-as-section`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            target_bit_id: selectedBitId,
            title: extractData.title || "From Performance",
            content: extractData.content,
            source_performance_id: performance.transcription_id,
            source_segment_id: extractData.segment?.joke_id
          })
        });

        if (response.ok) {
          const data = await response.json();
          toast.success("Section added to Bit");
          // Wait for bits to refresh before switching
          await onBitCreated?.();
          // Now switch to write with fresh data
          onSwitchToWrite?.(selectedBitId);
        } else {
          const error = await response.json();
          toast.error(error.detail || "Failed to add section");
        }
      } else {
        const response = await fetch(`${API}/review/extract-as-bit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: extractData.title,
            content: extractData.content,
            source_performance_id: performance.transcription_id,
            source_segment_id: extractData.segment?.joke_id
          })
        });

        if (response.ok) {
          const data = await response.json();
          toast.success("New Bit created");
          onBitCreated?.();
          onSwitchToWrite?.(data.bit.bit_id);
        } else {
          const error = await response.json();
          toast.error(error.detail || "Failed to create Bit");
        }
      }
    } catch (error) {
      console.error("Extract error:", error);
      toast.error("Failed to extract");
    } finally {
      setIsExtractDialogOpen(false);
      setExtractData(null);
    }
  };

  // Open note dialog
  const handleAddNote = (segment, text) => {
    setNoteTarget({ segment, text });
    setNoteText("");
    setIsNoteDialogOpen(true);
  };

  // Save performance note
  const handleSaveNote = async () => {
    if (!noteText.trim() || !performance?.transcription_id) return;

    try {
      const currentNotes = performance.performance_notes || [];
      const newNote = {
        note_id: `pnote_${Date.now()}`,
        segment_id: noteTarget?.segment?.joke_id,
        segment_title: noteTarget?.segment?.title,
        text: noteText.trim(),
        selected_text: noteTarget?.text || null,
        created_at: new Date().toISOString()
      };

      const response = await fetch(`${API}/transcriptions/${performance.transcription_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          performance_notes: [...currentNotes, newNote]
        })
      });

      if (response.ok) {
        toast.success("Note added");
        onBitCreated?.(); // Refresh
      }
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setIsNoteDialogOpen(false);
      setNoteText("");
      setNoteTarget(null);
    }
  };

  // Mark for rewrite
  const handleMarkForRewrite = async (segment) => {
    if (!segment || !performance?.transcription_id) return;

    try {
      const response = await fetch(
        `${API}/review/mark-for-rewrite/${performance.transcription_id}/${segment.joke_id}`,
        { method: 'POST', credentials: 'include' }
      );

      if (response.ok) {
        toast.success("Marked for rewrite");
      }
    } catch (error) {
      toast.error("Failed to mark for rewrite");
    }
  };

  if (!performance) return null;

  // Calculate available height (viewport - header - tabs)
  const panelHeight = 'calc(100vh - 140px)';

  return (
    <div className="flex flex-row w-full" style={{ height: panelHeight }}>
      {/* Left Panel: Transcript */}
      <div className="w-[60%] border-r border-border flex flex-col" style={{ height: panelHeight }}>
        {/* Header */}
        <div className="p-4 border-b border-border shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{performance.filename}</h3>
              <p className="text-xs text-muted-foreground">
                {new Date(performance.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-muted rounded-md p-0.5">
                <button
                  onClick={() => setViewMode('original')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    viewMode === 'original' 
                      ? 'bg-background shadow-sm font-medium' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Original
                </button>
                <button
                  onClick={() => setViewMode('analysis')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    viewMode === 'analysis' 
                      ? 'bg-background shadow-sm font-medium' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Analysis
                </button>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefreshAnalysis}
                disabled={isRefreshing}
                className="gap-1"
              >
                <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                Re-analyze
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Transcript - Scrollable */}
        <div style={{ flex: 1, overflowY: 'scroll' }}>
          {viewMode === 'original' ? (
            // Original view - plain text
            <div className="font-mono text-sm leading-relaxed p-4 whitespace-pre-wrap">
              {performance.transcript}
            </div>
          ) : (
            // Analysis view - highlighted
            <InteractiveTranscript
              transcript={performance.transcript}
              analysis={analysis}
              activeSegmentId={activeSegmentId}
              onSegmentClick={handleSegmentClick}
              onTextSelect={handleTextSelect}
              scrollTargetRef={scrollTargetRef}
              filterType={filterType}
              onFilterChange={setFilterType}
            />
          )}
        </div>
      </div>

      {/* Right Panel: Set Map & Actions - Scrollable */}
      <div className="w-[40%] bg-card flex flex-col" style={{ height: panelHeight }}>
        <div style={{ flex: 1, overflowY: 'scroll' }}>
          {/* 1. Set Map (Navigation) */}
          <div className="border-b border-border">
            <SetMap
              segments={jokes}
              activeSegmentId={activeSegmentId}
              onSegmentClick={handleSegmentClick}
              transcriptionId={performance.transcription_id}
              onCreateAllBits={handleCreateAllBits}
              isCreatingBits={isCreatingBits}
              onRunAnalysis={handleRunAnalysis}
              isAnalyzing={isAnalyzing}
              analysisExists={analysisExists}
            />
          </div>

          {/* 2. Segment Detail (Mechanical Facts) */}
          <div className="border-b border-border">
            <SegmentDetail
              segment={activeSegment}
              rawSegments={segments}
            />
          </div>

          {/* 3. Density (Directional) */}
          <div className="border-b border-border">
            <DensityIndicator
              segment={activeSegment}
              allSegments={jokes}
            />
          </div>

          {/* 4. Actions */}
          <ActionsPanel
            segment={activeSegment}
            selectedText={selectedText}
            onExtractAsSection={handleExtractAsSection}
            onExtractAsNewBit={handleExtractAsNewBit}
            onAddNote={handleAddNote}
            onMarkForRewrite={handleMarkForRewrite}
            bits={bits}
          />
        </div>
      </div>

      {/* Extract Dialog */}
      <Dialog open={isExtractDialogOpen} onOpenChange={setIsExtractDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              {extractMode === "section" ? "Extract as Section" : "Extract as New Bit"}
            </DialogTitle>
            <DialogDescription>
              {extractMode === "section" 
                ? "Add this text to an existing Bit as a new section"
                : "Create a new Bit from this text"
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Preview */}
            <div className="bg-muted/50 p-3 rounded-sm max-h-32 overflow-auto">
              <p className="text-sm font-mono line-clamp-4">{extractData?.content}</p>
            </div>

            {/* Mode Toggle */}
            <RadioGroup value={extractMode} onValueChange={setExtractMode}>
              <div className="flex items-center space-x-3 p-2 rounded-sm border border-border">
                <RadioGroupItem value="section" id="section" />
                <Label htmlFor="section" className="cursor-pointer flex-1">
                  Add to existing Bit
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-sm border border-border">
                <RadioGroupItem value="new" id="new" />
                <Label htmlFor="new" className="cursor-pointer flex-1">
                  Create new Bit
                </Label>
              </div>
            </RadioGroup>

            {/* Bit Selector (for section mode) */}
            {extractMode === "section" && bits && bits.length > 0 && (
              <Select value={selectedBitId} onValueChange={setSelectedBitId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a Bit" />
                </SelectTrigger>
                <SelectContent>
                  {bits.map(bit => (
                    <SelectItem key={bit.bit_id} value={bit.bit_id}>
                      {bit.title || 'Untitled Bit'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExtractDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmExtract}
              disabled={extractMode === "section" && !selectedBitId}
            >
              {extractMode === "section" ? "Add Section" : "Create Bit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-4 h-4" />
              Add Performance Note
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {noteTarget?.segment && (
              <div className="text-xs text-muted-foreground">
                For: <span className="font-medium">{noteTarget.segment.title}</span>
              </div>
            )}
            {noteTarget?.text && (
              <div className="bg-muted/50 p-2 rounded-sm">
                <p className="text-xs font-mono line-clamp-2">{noteTarget.text}</p>
              </div>
            )}
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Short, factual note about delivery or context..."
              className="min-h-[100px]"
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNote} disabled={!noteText.trim()}>
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SetReview;
